import { type HUImageDto, HUImageDtoSchema } from "@modules/dtos/imageDto"
import { type PublicProfileDto } from "@modules/profile/publicProfile/gateway/publicProfileDto"
import { UserDtoSchema } from "@modules/profile/userProfile/userDto"
import { z } from "zod"

export enum BoxStaffPermissions {
  // DASHBOARD
  EDIT_DASHBOARD = "EDIT_DASHBOARD",
  // PLANNING
  SEE_TIMETABLES = "SEE_TIMETABLES",
  CREATE_ACTIVITY = "CREATE_ACTIVITY",
  MANAGE_SLOTS = "MANAGE_SLOTS",
  MANAGE_TYPICAL_WEEK = "MANAGE_TYPICAL_WEEK",
  IMPORT_PROGRAMMING = "IMPORT_PROGRAMMING",
  // PROGRAMMING
  MANAGE_PROGRAMMING = "MANAGE_PROGRAMMING",
  // OFFER
  SEE_OFFERS = "SEE_OFFERS",
  CREATE_OFFER = "CREATE_OFFER",
  EDIT_OFFER = "EDIT_OFFER",
  DELETE_OFFER = "DELETE_OFFER",
  // MEMBERS
  SEE_MEMBERS = "SEE_MEMBERS",
  EDIT_MEMBER = "EDIT_MEMBER",
  EDIT_MEMBER_OFFER = "EDIT_MEMBER_OFFER",
  ARCHIVE_MEMBER = "ARCHIVE_MEMBER",
  SEE_TERMINATED = "SEE_TERMINATED",
  SEND_OFFER = "SEND_OFFER",
  SEE_STAFF = "SEE_STAFF",
  ADD_STAFF = "ADD_STAFF",
  CHANGE_ROLE_STAFF = "CHANGE_ROLE_STAFF",
  REMOVE_STAFF = "REMOVE_STAFF",
  // SHOP
  SEE_SHOP = "SEE_SHOP",
  ADD_ITEM = "ADD_ITEM",
  EDIT_ITEM = "EDIT_ITEM",
  SALE_ITEM = "SALE_ITEM",
  SEE_INVENTORY = "SEE_INVENTORY",
  // LEADS
  SEE_LEADS = "SEE_LEADS",
  MANAGE_LEAD = "MANAGE_LEAD",
  // FINANCIAL
  SEE_FINANCIAL = "SEE_FINANCIAL",
  EDIT_FINANCIAL = "EDIT_FINANCIAL",
  // ADMIN
  SEE_GYM_INFORMATION = "SEE_GYM_INFORMATION",
  EDIT_GYM_INFORMATION = "EDIT_GYM_INFORMATION",
  DELETE_GYM = "DELETE_GYM",
  SEE_BANK_DETAILS = "SEE_BANK_DETAILS",
  EDIT_BANK_DETAILS = "EDIT_BANK_DETAILS",
}

export enum BoxStaffStatus {
  ACCEPTED = "ACCEPTED",
  PENDING = "PENDING",
  TERMINATED = "TERMINATED",
}

export enum BoxStaffActionRequest {
  ACCEPT = "ACCEPT",
  REFUSE = "REFUSE",
  QUIT = "QUIT",
}

export enum BoxStaffRole {
  OWNER = "OWNER",
  CO_OWNER = "CO_OWNER",
  HEAD_COACH = "HEAD_COACH",
  COACH = "COACH",
  FINANCIAL = "FINANCIAL",
  READ_ONLY = "READ_ONLY",
}

export const BoxStaffLightDtoSchema = z
  .object({
    staffMemberId: z.string(),
    staffMemberRole: z.enum(BoxStaffRole),
    user: UserDtoSchema,
  })
  .meta({ id: "BoxStaffLightDtoSchema" })

export const BoxStaffAddInputSchema = z
  .object({
    userId: z.string().nullish(),
    userEmail: z.email().nullish(),
    role: z.enum(BoxStaffRole),
  })
  .meta({ id: "BoxStaffAddInputSchema" })

export const BoxStaffDetailedUpadteInputSchema = z
  .object({
    internalNote: z.string().optional(),
    role: z.enum(BoxStaffRole).optional(),
  })
  .meta({ id: "BoxStaffDetailedUpadteInputSchema" })

export const BoxStaffItemDtoSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    boxId: z.string(),
    role: z.enum(BoxStaffRole),
    status: z.enum(BoxStaffStatus),
    firstname: z.string().nullish(),
    lastname: z.string().nullish(),
    email: z.email(),
    avatar: HUImageDtoSchema.nullish(),
    creationDate: z.iso.datetime(),
    username: z.string(),
    internalNote: z.string().nullish(),
    archived: z.boolean(),
    archivedDate: z.iso.datetime().nullish(),
  })
  .meta({ id: "BoxStaffItemDtoSchema" })

export type BoxStaffDetailedDto = {
  id: string
  userId: string
  boxId: string
  role: BoxStaffRole
  status: BoxStaffStatus
  firstname?: string
  lastname?: string
  email: string
  avatar?: HUImageDto
  creationDate: string
  username: string
  internalNote?: string
  archived: boolean
  publicProfile?: PublicProfileDto
}

export const BoxStaffInvitationDetailsDtoSchema = z
  .object({
    inviter: UserDtoSchema.nullable(),
    boxId: z.string(),
    role: z.enum(BoxStaffRole),
    status: z.enum(BoxStaffStatus),
  })
  .meta({ id: "BoxStaffInvitationDetailsDtoSchema" })

export type BoxStaffAddInput = z.infer<typeof BoxStaffAddInputSchema>
export type BoxStaffDetailedUpadteInput = z.infer<typeof BoxStaffDetailedUpadteInputSchema>
export type BoxStaffItemDto = z.infer<typeof BoxStaffItemDtoSchema>
export type BoxStaffLightDto = z.infer<typeof BoxStaffLightDtoSchema>
export type BoxStaffInvitationDetailsDto = z.infer<typeof BoxStaffInvitationDetailsDtoSchema>

export enum StaffColumnToSort {
  NAME = "NAME",
  LAST_POSITION = "LAST_POSITION",
  ROLE = "ROLE",
  CREATION_DATE = "CREATION_DATE",
  ARCHIVED_DATE = "ARCHIVED_DATE",
}
